import {
  GET_ALL_PUBLISHED_PROGRAMS,
  GET_PROGRAMS_BY_MONTH,
  SET_AVAIL_DATE,
  SET_BLOCK_DATE,
  GET_HOLIDAYS_BY_MONTH,
  ADD_HOLIDAY,
  REMOVE_HOLIDAY,
} from "../types";

export interface PublishedProgram {
  prog_id: string;
  title: string;
  /** Present on adminCalendar day entries: is the program bookable that day. */
  available?: boolean;
  /** How many enrollments begin on this date. */
  enrollments_starting?: number;
  /** Time-of-day window, "HH:mm" or "HH:mm:ss". Not sent by adminCalendar as of
   *  the payload seen on 2026-08-31 — the times live on the program's fee tiers
   *  (`prog_flat_fee_details.start_time`/`end_time`). Declared optional here so
   *  the calendar renders them the moment either source supplies them. */
  start_time?: string | null;
  end_time?: string | null;
  /** Some responses nest the tiers instead of flattening the times. */
  fee_details?: Array<{ start_time?: string | null; end_time?: string | null }>;
  flat_fee_details?: Array<{ start_time?: string | null; end_time?: string | null }>;
}

/** An appointment sitting on a calendar day, from adminCalendar's `sessions[]`. */
export interface DaySession {
  session_id?: string;
  title?: string;
  start_time?: string;
  session_type?: string;
  session_mode?: string;
  status?: string;
}

export interface DayPrograms {
  date: string;
  programs: PublishedProgram[];
  /** adminCalendar returns this for every day; older responses omit it. */
  sessions?: DaySession[];
}

export interface HolidayEntry {
  holiday_date: string;
  reason?: string;
}

interface CalendarState {
  publishedPrograms: PublishedProgram[];
  programsByMonth: Record<string, DayPrograms[]>;
  holidaysByMonth: Record<string, HolidayEntry[]>;
  loading: {
    published: boolean;
    month: boolean;
    avail: boolean;
    block: boolean;
    holidays: boolean;
    addHoliday: boolean;
    removeHoliday: boolean;
  };
  error: string | null;
}

const initialState: CalendarState = {
  publishedPrograms: [],
  programsByMonth: {},
  holidaysByMonth: {},
  loading: {
    published: false,
    month: false,
    avail: false,
    block: false,
    holidays: false,
    addHoliday: false,
    removeHoliday: false,
  },
  error: null,
};

function monthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

export default function calendarReducer(
  state: CalendarState = initialState,
  action: any
): CalendarState {
  switch (action.type) {
    case `${GET_ALL_PUBLISHED_PROGRAMS}_REQUEST`:
      return {
        ...state,
        loading: { ...state.loading, published: true },
        error: null,
      };
    case `${GET_ALL_PUBLISHED_PROGRAMS}_SUCCESS`:
      return {
        ...state,
        publishedPrograms: action.payload ?? [],
        loading: { ...state.loading, published: false },
        error: null,
      };
    case `${GET_ALL_PUBLISHED_PROGRAMS}_FAILURE`:
      return {
        ...state,
        loading: { ...state.loading, published: false },
        error: action.payload?.message ?? "Failed to load programs",
      };

    case `${GET_PROGRAMS_BY_MONTH}_REQUEST`:
      return {
        ...state,
        loading: { ...state.loading, month: true },
        error: null,
      };
    case `${GET_PROGRAMS_BY_MONTH}_SUCCESS`: {
      const key = action.meta?.monthKey ?? "";
      const list = action.payload ?? [];
      return {
        ...state,
        programsByMonth: { ...state.programsByMonth, [key]: list },
        loading: { ...state.loading, month: false },
        error: null,
      };
    }
    case `${GET_PROGRAMS_BY_MONTH}_FAILURE`:
      return {
        ...state,
        loading: { ...state.loading, month: false },
        error: action.payload?.message ?? "Failed to load month data",
      };

    case `${SET_AVAIL_DATE}_REQUEST`:
      return { ...state, loading: { ...state.loading, avail: true }, error: null };
    case `${SET_AVAIL_DATE}_SUCCESS`:
      return { ...state, loading: { ...state.loading, avail: false }, error: null };
    case `${SET_AVAIL_DATE}_FAILURE`:
      return {
        ...state,
        loading: { ...state.loading, avail: false },
        error: action.payload?.message ?? "Failed to set available date",
      };

    case `${SET_BLOCK_DATE}_REQUEST`:
      return { ...state, loading: { ...state.loading, block: true }, error: null };
    case `${SET_BLOCK_DATE}_SUCCESS`: {
      // Optimistic: remove program from that date in programsByMonth
      const { monthKey: mk, date, prog_id } = action.meta ?? {};
      if (!mk || !date || !prog_id) {
        return { ...state, loading: { ...state.loading, block: false }, error: null };
      }
      const norm = (d: string) => (typeof d === "string" ? d.slice(0, 10) : d);
      const monthData = state.programsByMonth[mk] ?? [];
      const updated = monthData.map((day) => {
        if (norm(day.date) !== date) return day;
        return {
          ...day,
          programs: day.programs.filter((p) => p.prog_id !== prog_id),
        };
      });
      return {
        ...state,
        programsByMonth: { ...state.programsByMonth, [mk]: updated },
        loading: { ...state.loading, block: false },
        error: null,
      };
    }
    case `${SET_BLOCK_DATE}_FAILURE`:
      return {
        ...state,
        loading: { ...state.loading, block: false },
        error: action.payload?.message ?? "Failed to block date",
      };

    case `${GET_HOLIDAYS_BY_MONTH}_REQUEST`:
      return {
        ...state,
        loading: { ...state.loading, holidays: true },
        error: null,
      };
    case `${GET_HOLIDAYS_BY_MONTH}_SUCCESS`: {
      const key = action.meta?.monthKey ?? "";
      const list = action.payload ?? [];
      return {
        ...state,
        holidaysByMonth: { ...state.holidaysByMonth, [key]: list },
        loading: { ...state.loading, holidays: false },
        error: null,
      };
    }
    case `${GET_HOLIDAYS_BY_MONTH}_FAILURE`:
      return {
        ...state,
        loading: { ...state.loading, holidays: false },
        error: action.payload?.message ?? "Failed to load holidays",
      };

    case `${ADD_HOLIDAY}_REQUEST`:
      return { ...state, loading: { ...state.loading, addHoliday: true }, error: null };
    case `${ADD_HOLIDAY}_SUCCESS`: {
      const { monthKey: mk } = action.meta ?? {};
      const entry = action.payload;
      if (!mk || !entry?.holiday_date) {
        return { ...state, loading: { ...state.loading, addHoliday: false }, error: null };
      }
      const dateNorm = typeof entry.holiday_date === "string" ? entry.holiday_date.slice(0, 10) : entry.holiday_date;
      const newEntry: HolidayEntry = { holiday_date: dateNorm, reason: entry.reason };
      const existing = state.holidaysByMonth[mk] ?? [];
      const updated = existing.some((h) => (typeof h.holiday_date === "string" ? h.holiday_date.slice(0, 10) : h.holiday_date) === dateNorm)
        ? existing
        : [...existing, newEntry];
      return {
        ...state,
        holidaysByMonth: { ...state.holidaysByMonth, [mk]: updated },
        loading: { ...state.loading, addHoliday: false },
        error: null,
      };
    }
    case `${ADD_HOLIDAY}_FAILURE`:
      return {
        ...state,
        loading: { ...state.loading, addHoliday: false },
        error: action.payload?.message ?? "Failed to add holiday",
      };

    case `${REMOVE_HOLIDAY}_REQUEST`:
      return { ...state, loading: { ...state.loading, removeHoliday: true }, error: null };
    case `${REMOVE_HOLIDAY}_SUCCESS`: {
      const { monthKey: mk, holiday_date } = action.meta ?? {};
      if (!mk || !holiday_date) {
        return { ...state, loading: { ...state.loading, removeHoliday: false }, error: null };
      }
      const norm = (d: string) => (typeof d === "string" ? d.slice(0, 10) : d);
      const monthData = state.holidaysByMonth[mk] ?? [];
      const updated = monthData.filter((h) => norm(h.holiday_date) !== holiday_date);
      return {
        ...state,
        holidaysByMonth: { ...state.holidaysByMonth, [mk]: updated },
        loading: { ...state.loading, removeHoliday: false },
        error: null,
      };
    }
    case `${REMOVE_HOLIDAY}_FAILURE`:
      return {
        ...state,
        loading: { ...state.loading, removeHoliday: false },
        error: action.payload?.message ?? "Failed to remove holiday",
      };

    default:
      return state;
  }
}

export { monthKey };
